import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PRD5011_R01Dto, PRD5011_R01ProductResultDto } from './Dto_PRD5011_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface PRD5011_R01Filter {
    compCd: string;
    kpicertkey: string;
    ocrdttmDateRange: DateRange | undefined;
    kpifldcd: string;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PRD5011_R01 {
    PRD5011_R01Filter: PRD5011_R01Filter;
    PRD5011_R01PastFilter: PRD5011_R01Filter;
    setPRD5011_R01Filter: (filter: Partial<PRD5011_R01Filter>) => void;
    setPRD5011_R01PastFilter: (filter: Partial<PRD5011_R01Filter>) => void;

    // === 리스트 관련 ===
    PRD5011_R01List: PRD5011_R01Dto[];
    setPRD5011_R01List: (list: PRD5011_R01Dto[]) => void;
    insertPRD5011_R01List: (data: PRD5011_R01Dto[], index?: number) => void;
    removePRD5011_R01List: (index: number) => void;
    updatePRD5011_R01List: (data: PRD5011_R01Dto, index: number) => void;
    
    // === 선택 관련 ===
    selectedPRD5011_R01: PRD5011_R01Dto | undefined;
    setSelectedPRD5011_R01: (data: PRD5011_R01Dto | undefined) => void;
    initSelectedPRD5011_R01: () => void;

	
    // === 생산실적 리스트 관련 ===
    PRD5011_R01ProductResultList: PRD5011_R01ProductResultDto[];
    setPRD5011_R01ProductResultList: (list: PRD5011_R01ProductResultDto[]) => void;
    insertPRD5011_R01ProductResultList: (data: PRD5011_R01ProductResultDto[], index?: number) => void;
    removePRD5011_R01ProductResultList: (index: number) => void;
    updatePRD5011_R01ProductResultList: (data: PRD5011_R01ProductResultDto, index: number) => void;
    
    // === 생산실적 선택 관련 ===
    selectedPRD5011_R01ProductResultList: string[];
    setSelectedPRD5011_R01ProductResultList: (idList: string[]) => void;
    addSelectedPRD5011_R01ProductResultList: (idList: string[]) => void;
    removeSelectedPRD5011_R01ProductResultList: (idList?: string[]) => void;
    initSelectedPRD5011_R01ProductResultList: () => void;
    initPRD5011_R01ProductResultData: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    productResultColumnState: ColumnState[] | null;
    setProductResultColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    detailScrollTop: number | null;
    setDetailScrollTop: (top: number | null) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD5011_R01Filter: PRD5011_R01Filter = {
    compCd: '',
    kpicertkey: '',
    ocrdttmDateRange: defaultDateRange,
    kpifldcd: '',
};

/**
 * Zustand 스토어 정의
 */
export const usePRD5011_R01Store = create<Store_PRD5011_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_PRD5011_R01, 'PRD5011_R01Filter' | 'PRD5011_R01PastFilter'>) => 
        (filter: Partial<PRD5011_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PRD5011_R01Filter: defaultPRD5011_R01Filter,
        PRD5011_R01PastFilter: defaultPRD5011_R01Filter,
        setPRD5011_R01Filter: createFilterSetter('PRD5011_R01Filter'),
        setPRD5011_R01PastFilter: createFilterSetter('PRD5011_R01PastFilter'),

        // === 리스트 관련 ===
        PRD5011_R01List: [],
        setPRD5011_R01List: (list: PRD5011_R01Dto[]) => set({ PRD5011_R01List: list }),
        insertPRD5011_R01List: (data: PRD5011_R01Dto[], index?: number) => set((state) => {
            const _list = [...state.PRD5011_R01List];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, PRD5011_R01List: _list };
        }),
        removePRD5011_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD5011_R01List: state.PRD5011_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD5011_R01List: (data: PRD5011_R01Dto, index: number) => set((state) => {
            const _list = [...state.PRD5011_R01List];
            _list[index] = data;
            return { ...state, PRD5011_R01List: _list };
        }),

        // === 선택 관련 ===
        selectedPRD5011_R01: undefined,
        setSelectedPRD5011_R01: (data: PRD5011_R01Dto | undefined) => set({
            selectedPRD5011_R01: data,
            detailScrollTop: null,
        }),
        initSelectedPRD5011_R01: () => set({ selectedPRD5011_R01: undefined }),

		

        // === 리스트 관련 ===
        PRD5011_R01ProductResultList: [],
        setPRD5011_R01ProductResultList: (list: PRD5011_R01ProductResultDto[]) => set({ PRD5011_R01ProductResultList: list }),
        insertPRD5011_R01ProductResultList: (data: PRD5011_R01ProductResultDto[], index?: number) => set((state) => {
            const _list = [...state.PRD5011_R01ProductResultList];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, PRD5011_R01ProductResultList: _list };
        }),
        removePRD5011_R01ProductResultList: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD5011_R01ProductResultList: state.PRD5011_R01ProductResultList.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD5011_R01ProductResultList: (data: PRD5011_R01ProductResultDto, index: number) => set((state) => {
            const _list = [...state.PRD5011_R01ProductResultList];
            _list[index] = data;
            return { ...state, PRD5011_R01ProductResultList: _list };
        }),

        // === 선택 관련 ===
        selectedPRD5011_R01ProductResultList: [],
        setSelectedPRD5011_R01ProductResultList: (idList: string[]) => set({ selectedPRD5011_R01ProductResultList: idList }),
        addSelectedPRD5011_R01ProductResultList: (idList: string[]) => set((state) => {
            const _selectedPRD5011_R01ProductResultList = [...state.selectedPRD5011_R01ProductResultList];
            _selectedPRD5011_R01ProductResultList.push(...idList.filter(value => !_selectedPRD5011_R01ProductResultList.includes(value)));
            return { ...state, selectedPRD5011_R01ProductResultList: _selectedPRD5011_R01ProductResultList };
        }),
        removeSelectedPRD5011_R01ProductResultList: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD5011_R01ProductResultList: [] };
            }
            
            return {
                ...state,
                selectedPRD5011_R01ProductResultList: removeItemsFromArray(state.selectedPRD5011_R01ProductResultList, idList),
            };
        }),
        initSelectedPRD5011_R01ProductResultList: () => set({ selectedPRD5011_R01ProductResultList: [] }),
        initPRD5011_R01ProductResultData: () => set({
            PRD5011_R01ProductResultList: [],
            selectedPRD5011_R01ProductResultList: [],
            detailScrollTop: null,
        }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),
        productResultColumnState: null,
        setProductResultColumnState: (state) => set({ productResultColumnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),
        detailScrollTop: null,
        setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            PRD5011_R01Filter: defaultPRD5011_R01Filter,
            PRD5011_R01PastFilter: defaultPRD5011_R01Filter,
            PRD5011_R01List: [],
            PRD5011_R01ProductResultList: [],
            selectedPRD5011_R01: undefined,
            selectedPRD5011_R01ProductResultList: [],
            columnState: null,
            productResultColumnState: null,
            allCount: 0,
            scrollTop: null,
            detailScrollTop: null,
		
        })),
    };
});
